/**
 * Formulario para crear un nuevo grupo de pedidos N2
 */
import React from 'react';
import {
    Box,
    Typography,
    Button,
    Stack,
    Chip,
    CircularProgress,
    Alert,
    TextField
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { BOTON_PRIMARIO_SX } from '../../../../components/ui/buttonStyles';
import { BADGE_COLORS, getBadgeStyles } from '../../../../components/ui/badgeColors';

/**
 * Formulario para crear grupos de pedidos N2.
 * Se renderiza como sección dentro del Card único de la página (sin Card propio).
 * @param {Object} props
 * @param {Array} props.pedidosSeleccionados - Array de pedidos seleccionados con info completa
 * @param {string} props.nombreGrupo - Nombre del grupo
 * @param {Function} props.setNombreGrupo - Setter para el nombre
 * @param {boolean} props.loading - Estado de carga
 * @param {Function} props.onCrearGrupo - Callback al crear grupo
 */
const FormularioCrearGrupo = ({
    pedidosSeleccionados,
    nombreGrupo,
    setNombreGrupo,
    loading,
    onCrearGrupo
}) => {
    if (pedidosSeleccionados.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                p: 2,
                border: '2px solid #cc071e',
                borderRadius: 1
            }}
        >
            <Typography variant="h6" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Crear Grupo
            </Typography>

            <Stack spacing={{ xs: 1.5, sm: 2 }}>
                {/* Cantidad de pedidos seleccionados: banda a ancho completo, no un Chip.
                    Reutiliza la paleta de badges para no salirse del sistema de color. */}
                <Typography
                    sx={{
                        ...getBadgeStyles(BADGE_COLORS.AMBER, { fontSize: 12 }),
                        height: 'auto',
                        px: 1.5,
                        py: 1,
                        borderRadius: 1
                    }}
                >
                    {pedidosSeleccionados.length} {pedidosSeleccionados.length === 1 ? 'pedido seleccionado' : 'pedidos seleccionados'}
                </Typography>

                {/* Lista de pedidos seleccionados como chips */}
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: { xs: 1, sm: 1.5 }, color: '#6c757d', fontSize: { xs: 12, sm: 13 } }}>
                        Pedidos en este grupo ({pedidosSeleccionados.length}):
                    </Typography>
                    {/* Mismo badge que la lista de pedidos de ListaGruposActivos */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 0.75 } }}>
                        {pedidosSeleccionados.slice(0, 8).map(p => (
                            <Chip
                                key={p.id}
                                label={p.num}
                                size="small"
                                sx={getBadgeStyles(BADGE_COLORS.BLUE, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                            />
                        ))}
                        {pedidosSeleccionados.length > 8 && (
                            <Chip
                                label={`+${pedidosSeleccionados.length - 8} más`}
                                size="small"
                                sx={getBadgeStyles(BADGE_COLORS.GRAY, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                            />
                        )}
                    </Box>
                </Box>

                {/* Alert informativo */}
                <Alert severity="info" sx={{ fontSize: { xs: 12, sm: 13 } }}>
                    Los productos de estos pedidos se agruparán al crear el grupo
                </Alert>

                {/* Formulario */}
                <Box>
                    <Typography variant="body2" sx={{ mb: { xs: 0.75, sm: 1 }, fontWeight: 600, fontSize: { xs: 12, sm: 13 } }}>
                        Nombre del grupo
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="Ej: Ruta 1 - Mañana"
                        value={nombreGrupo}
                        onChange={(e) => setNombreGrupo(e.target.value)}
                        disabled={loading}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !loading) {
                                e.preventDefault();
                                onCrearGrupo();
                            }
                        }}
                        sx={{
                            mb: { xs: 1.5, sm: 2 },
                            '& .MuiInputBase-input': {
                                fontSize: { xs: 14, sm: 15 }
                            }
                        }}
                    />

                    <Button
                        type="button"
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        onClick={onCrearGrupo}
                        startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <GroupAddIcon />}
                        sx={BOTON_PRIMARIO_SX}
                    >
                        {loading ? 'Creando grupo...' : 'Crear grupo'}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

export default FormularioCrearGrupo;
